import { useEffect, useRef } from 'react';
import { JournalText, Search } from 'react-bootstrap-icons';

import { Col, ColProps, Input, Row, Space, Spin } from 'antd';
import { Gutter } from 'antd/lib/grid/row';

import EmptyState from '@/components/shared/EmptyState';
import PageTitle from '@/components/shared/PageTitle';
import SkeletonBaseCard from '@/components/shared/SkeletonBaseCard';
import Wrapper from '@/components/shared/Wrapper';
import { useDebounce } from '@/hooks/useDebounce';
import { useWindowScrollReachedEnd } from '@/hooks/useWindowScrollReachedEnd';
import { IPaginationParams } from '@/interfaces/pagination';
import { notesApi } from '@/services/notes';

import Card from './Card';
import { NotesContextProvider } from './context';
import NotesPreview from './Preview';

export type FiltersNotes = {
  page?: number;
  size?: number;
  search?: string;
  order?: 'ASC' | 'DESC';
};

const DEFAULT_ROWS_SPACING = [16, 16] as [Gutter, Gutter];

const DEFAULT_COLUMNS_WIDTH: Pick<ColProps, 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'> = {
  xs: 24,
  sm: 12,
  md: 12,
  lg: 8,
  xl: 6,
  xxl: 6
};

const Notes = () => {
  const filters = useRef<Partial<IPaginationParams>>({ page: 1 });
  const { debounce } = useDebounce();
  const [refetch, { data: notes, isFetching }] = notesApi.endpoints.getAllNotations.useLazyQuery();
  const reachedEnd = useWindowScrollReachedEnd();
  const hasNotes = notes?.data.length > 0;

  const handleSearch = (event: React.ChangeEvent<{ value: string }>) => {
    debounce(() => {
      refetch({ search: event.target.value || undefined });
    }, 800);
  };

  useEffect(() => {
    if (!notes || isFetching || !reachedEnd) return;

    if (notes.total_pages > notes.page.page) {
      refetch({
        page: (notes.page.page ?? 0) + 1
      });
    }
  }, [reachedEnd, notes, isFetching, refetch]);

  useEffect(() => {
    refetch(filters.current);
  }, [refetch]);

  return (
    <Wrapper>
      <NotesContextProvider>
        <PageTitle
          title='Minhas Anotações'
          endContent={
            <Space size={16}>
              <Input
                size='large'
                placeholder='Buscar'
                prefix={<Search size={12} />}
                allowClear
                onChange={handleSearch}
              />
            </Space>
          }
        />

        {isFetching && !hasNotes && (
          <Row gutter={DEFAULT_ROWS_SPACING}>
            {new Array(8).fill('').map((_, index) => (
              <Col {...DEFAULT_COLUMNS_WIDTH} key={index}>
                <SkeletonBaseCard />
              </Col>
            ))}
          </Row>
        )}

        {!isFetching && !hasNotes && (
          <EmptyState
            icon={<JournalText />}
            title='Suas anotações aparecerão aqui...'
            description='Aqui você poderá acessar com facilidade todas as anotações feitas nas aulas.'
          />
        )}

        {isFetching && hasNotes && (
          <>
            <Row gutter={DEFAULT_ROWS_SPACING}>
              {notes?.data.map((note, key) => (
                <Col {...DEFAULT_COLUMNS_WIDTH} key={key}>
                  <Card {...note} />
                </Col>
              ))}
            </Row>
            <Spin style={{ alignSelf: 'center', display: 'block' }} size='large' />
          </>
        )}

        {!isFetching && hasNotes && (
          <Row gutter={DEFAULT_ROWS_SPACING}>
            {notes?.data.map((note, key) => (
              <Col {...DEFAULT_COLUMNS_WIDTH} key={key}>
                <Card {...note} />
              </Col>
            ))}
          </Row>
        )}

        <NotesPreview />
      </NotesContextProvider>
    </Wrapper>
  );
};

export default Notes;
